import React, { useEffect, useState } from 'react';
import { fetchOrders } from '../api/airtable';
import users from '../config/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faMoneyBill1Wave, faDownload } from '@fortawesome/free-solid-svg-icons';
import html2canvas from 'html2canvas';
import '../styles/Billing.css';

const getAdditionalLaborCostByDistance = (distance) => {
    if (distance < 300) return 0;
    if (distance >= 300 && distance < 320) return 0;
    if (distance >= 320 && distance < 370) return 100;
    if (distance >= 370 && distance < 420) return 200;
    if (distance >= 420 && distance < 485) return 300;
    if (distance >= 485 && distance < 520) return 800;
    if (distance >= 520 && distance < 625) return 1300;
    if (distance >= 625 && distance < 660) return 1600;
    if (distance >= 660 && distance < 695) return 1700;
    if (distance >= 695 && distance < 730) return 1900;
    if (distance >= 730 && distance < 765) return 2100;
    if (distance >= 765 && distance < 800) return 2200;
    if (distance >= 800) return 2400;
};

const Billing = ({ userRole, carNumber, username, pageTitle }) => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [cashOrders, setCashOrders] = useState([]);
    const [transferOrders, setTransferOrders] = useState([]);
    const [showCashList, setShowCashList] = useState(false);
    const [showTransferList, setShowTransferList] = useState(false);
    const [hasIncompleteTasks, setHasIncompleteTasks] = useState(false);
    const [otherExpensesNote, setOtherExpensesNote] = useState("");

    const currentUser = users.find(user => user.username === username);
    const pointRate = currentUser?.pointRate || 0;
    const kiloRate = currentUser?.kiloRate || 0;
    const baselaborCost = currentUser?.laborCost || 0;

    const [expenses, setExpenses] = useState({
        fuelCost: 0,
        laborCost: baselaborCost,
        otherExpenses: 0, 
        kiloDistance: 0, 
    });

    useEffect(() => {
        const getOrders = async () => {
            const allOrders = await fetchOrders();

            const now = new Date();
            const currentHours = now.getHours();
            let targetDate = now.toLocaleDateString("th-TH");

            if (currentHours >= 0 && currentHours < 4) {
                const yesterday = new Date();
                yesterday.setDate(now.getDate() - 1);
                targetDate = yesterday.toLocaleDateString("th-TH");
            }

            const completedStatuses = ["จ่ายแล้ว ส่งแล้ว", "โอนแล้ว ส่งแล้ว", "ไม่รับ", "ไม่ส่ง", "ไม่ตอบ", "ไม่รับเลื่อนส่ง"];

            const hasIncompleteTasksFlag = allOrders.some(order => {
                const orderDate = new Date(order.fields['กำหนดส่ง']).toLocaleDateString("th-TH");
                const isCarMatched = order.fields['เบอร์รถ'] && order.fields['เบอร์รถ'].trim() === String(carNumber).trim();
                const isTodayOrYesterday = orderDate === targetDate;
                const isIncomplete = !completedStatuses.includes(order.fields['สถานะ']);
                return isCarMatched && isTodayOrYesterday && isIncomplete;
            });
            setHasIncompleteTasks(hasIncompleteTasksFlag);

            const cash = allOrders.filter(order => {
                const orderDate = new Date(order.fields['กำหนดส่ง']).toLocaleDateString("th-TH");
                const isCash = order.fields['สถานะ'] === 'จ่ายแล้ว ส่งแล้ว';
                const isTargetDate = orderDate === targetDate;
                const isCarMatched = order.fields['เบอร์รถ'] && order.fields['เบอร์รถ'].trim() === String(carNumber).trim();
                return isCash && isTargetDate && isCarMatched;
            });

            const transfer = allOrders.filter(order => {
                const orderDate = new Date(order.fields['กำหนดส่ง']).toLocaleDateString("th-TH");
                const isTransfer = order.fields['สถานะ'] === 'โอนแล้ว ส่งแล้ว';
                const isTargetDate = orderDate === targetDate;
                const isCarMatched = order.fields['เบอร์รถ'] && order.fields['เบอร์รถ'].trim() === String(carNumber).trim();
                return isTransfer && isTargetDate && isCarMatched;
            });

            cash.sort((a, b) => (a.fields['ลำดับส่ง'] || 0) - (b.fields['ลำดับส่ง'] || 0));
            transfer.sort((a, b) => (a.fields['ลำดับส่ง'] || 0) - (b.fields['ลำดับส่ง'] || 0));

            setCashOrders(cash);
            setTransferOrders(transfer);
            setOrders([...cash, ...transfer].sort((a, b) => (b.fields['ลำดับส่ง'] || 0) - (a.fields['ลำดับส่ง'] || 0)));
            setIsLoading(false);
        };

        getOrders();
    }, [carNumber]);

    const calculateTotal = (ordersList) => {
        return ordersList.reduce((total, order) => total + (order.fields['รวมเป็นเงิน'] || 0), 0);
    };

    const totalCash = calculateTotal(cashOrders);
    const totalTransfer = calculateTotal(transferOrders);
    const totalCollected = totalCash + totalTransfer;

    const handleExpenseChange = (e) => {
        const { name, value } = e.target;
        setExpenses(prevExpenses => {
            let updated = { ...prevExpenses, [name]: parseFloat(value) || 0 };
            if (name === 'kiloDistance') {
                const additionalLaborCost = getAdditionalLaborCostByDistance(parseFloat(value) || 0);
                updated.laborCost = baselaborCost;
                updated.additionalLaborCost = additionalLaborCost;
            }
            return updated;
        });
    };

    const calculatePointEarnings = () => {
        const points = cashOrders.length + transferOrders.length;
        return points * pointRate;
    };

    const calculateTotalIncome = () => {
        const pointEarnings = calculatePointEarnings();
        const additionalLaborCost = getAdditionalLaborCostByDistance(expenses.kiloDistance);
        const totalIncome = baselaborCost + pointEarnings + additionalLaborCost;
        return {
            pointEarnings,
            additionalLaborCost,
            totalIncome,
        };
    };

    const handleOtherExpensesNoteChange = (e) => {
        setOtherExpensesNote(e.target.value);
    };

    const { pointEarnings, additionalLaborCost, totalIncome } = calculateTotalIncome();
    const totalExpenses = expenses.fuelCost + expenses.otherExpenses;
    const amountToSend = totalCash - totalIncome - totalExpenses;

    const handleCaptureBill = () => {
        const element = document.querySelector('.billing-container');
        if (element) {
            html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 2,
                useCORS: true
            }).then((canvas) => {
                const link = document.createElement('a');
                link.href = canvas.toDataURL('image/png');
                link.download = 'billing.png';
                link.click();
            }).catch((error) => {
                console.error('Failed to capture order:', error);
            });
        } else {
            console.error('Element is not found or already removed from DOM');
        }
    };

    return (
        <div>
            <header className="page-header">
                <h1>{`${pageTitle} รถเบอร์  ${carNumber}`}</h1>
            </header>
            {isLoading ? (
                <div className="load-orders-message-container">
                    <FontAwesomeIcon icon={faMoneyBill1Wave} className="load-orders-icon load-orders-paper" />
                    <p className="load-orders-message">กำลังโหลดข้อมูล...</p>
                </div>
            ) : (
                <div className="billing-container">
                    {hasIncompleteTasks && (
                        <p className="warning-message" style={{ color: 'red' }}>
                            คุณยังมีงานค้างที่รอส่ง กรุณาปิดงานให้จบก่อน นำส่งยอด
                        </p>
                    )}
                    <button className="captureBtn fr" onClick={handleCaptureBill}>
                        <FontAwesomeIcon icon={faDownload} /> แคปการคิดเงิน (ใส่ข้อมูลให้เรียบร้อยก่อน)
                    </button>
                    <div className="net-amount-container">
                        <h2>ยอดที่ต้องนำส่งร้านร้าน</h2>
                        <h1 className="net-amount">฿{amountToSend.toLocaleString()}</h1>
                    </div>
                    <h2>ยอดรวมเงินทั้งหมด: ฿{totalCollected.toLocaleString()}</h2>
                    
                    {/* Cash Orders Section */}
                    <div className="section">
                        <div onClick={() => setShowCashList(!showCashList)} className="toggle-section">
                            <h3>เงินสด: ฿{totalCash.toLocaleString()}</h3>
                            <FontAwesomeIcon icon={showCashList ? faChevronUp : faChevronDown} />
                        </div>
                        {showCashList && (
                            <ul className="order-list">
                                {cashOrders.map(order => (
                                    <li key={order.id}>
                                        คิวที่ {order.fields['ลำดับส่ง']} {order.fields['ชื่อลูกค้า'] || order.fields['ชื่อออเดอร์'].split('-')[0]} <span className='fr'>฿{order.fields['รวมเป็นเงิน'].toLocaleString()}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Transfer Orders Section */}
                    <div className="section">
                        <div onClick={() => setShowTransferList(!showTransferList)} className="toggle-section">
                            <h3>เงินโอน: ฿{totalTransfer.toLocaleString()}</h3>
                            <FontAwesomeIcon icon={showTransferList ? faChevronUp : faChevronDown} />
                        </div>
                        {showTransferList && (
                            <ul className="order-list">
                                {transferOrders.map(order => (
                                    <li key={order.id}>
                                        คิวที่ {order.fields['ลำดับส่ง']} {order.fields['ชื่อลูกค้า'] || order.fields['ชื่อออเดอร์'].split('-')[0]} <span className='fr'>฿{order.fields['รวมเป็นเงิน'].toLocaleString()}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <h4 className='red'>ใส่รายจ่าย</h4>
                    <div className="expense-form">
                        {!pointRate && (
                            <>
                                <label>ค่าน้ำมัน:</label>
                                <input type="number" name="fuelCost" onChange={handleExpenseChange} />
                            </>
                        )}

                        <label>ค่าใช้จ่ายอื่นๆ:</label>
                        <input type="number" name="otherExpenses" onChange={handleExpenseChange} />
                        {expenses.otherExpenses > 0 && (
                            <>
                                <label>หมายเหตุรายจ่ายอื่นๆ:</label>
                                <textarea
                                    value={otherExpensesNote}
                                    onChange={handleOtherExpensesNoteChange}
                                    rows={10}
                                    style={{ width: '100%' }}
                                    placeholder='ใส่รายละเอียดรายจ่ายอื่นๆ'
                                />
                            </>
                        )}

                        {pointRate > 0 && (
                            <>
                                <label>จำนวนกิโลเมตร:</label>
                                <input type="number" name="kiloDistance" onChange={handleExpenseChange} />
                            </>
                        )}
                    </div>

                    {baselaborCost > 0 && <p>ค่าแรง: ฿{baselaborCost.toLocaleString()}</p>}
                    {pointRate > 0 && (
                        <>
                            <p>ค่าจุด: ฿{pointEarnings.toLocaleString()} ({cashOrders.length + transferOrders.length}*฿{pointRate})</p>
                            <p>ค่าจ้างตามระยะทาง: ฿{additionalLaborCost.toLocaleString()}</p>
                            <p className='total-income'>
                                <span>รวมรายรับ รถเบอร์ {carNumber} </span> 
                                <span>฿{totalIncome.toLocaleString()}</span>
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Billing;
